import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api-client";
import { toApiFailure } from "../../lib/errors";
import type { components } from "../../lib/api-types.gen";
import { incidentKeys } from "../incidents/api";

/* Contract-derived types (ADR-S1-005). Hand-authored DTOs are forbidden; every
 * shape below is re-exported from the generated openapi-typescript output. */
export type PostmortemResponse = components["schemas"]["PostmortemResponse"];
export type FollowupTaskResponse = components["schemas"]["FollowupTaskResponse"];
export type FollowupStatus = components["schemas"]["FollowupStatus"];
export type UpsertPostmortemRequest =
  components["schemas"]["UpsertPostmortemRequest"];
export type CreateFollowupTaskRequest =
  components["schemas"]["CreateFollowupTaskRequest"];
export type UpdateFollowupTaskRequest =
  components["schemas"]["UpdateFollowupTaskRequest"];
export type ChangeFollowupTaskStatusRequest =
  components["schemas"]["ChangeFollowupTaskStatusRequest"];

export const postmortemKeys = {
  all: ["postmortem"] as const,
  byIncident: (incidentId: string) => ["postmortem", incidentId] as const,
};

/**
 * GET /incidents/{id}/postmortem. A 404 means "no postmortem exists yet"
 * (DO-004: at most one per incident) and is surfaced as `null`, NOT as a
 * query error - the detail card and the editor both branch on it.
 */
export function usePostmortem(incidentId: string) {
  return useQuery({
    queryKey: postmortemKeys.byIncident(incidentId),
    queryFn: async (): Promise<PostmortemResponse | null> => {
      try {
        return await apiFetch<PostmortemResponse>(
          `/incidents/${incidentId}/postmortem`,
        );
      } catch (error) {
        if (toApiFailure(error).status === 404) return null;
        throw error;
      }
    },
    enabled: incidentId.length > 0,
  });
}

/**
 * PUT /incidents/{id}/postmortem (F-007 upsert). Server rule: only the
 * incident's Owner or an Admin; create requires status geloest/beobachtet.
 * Also refreshes the timeline (the first save emits postmortem_erstellt).
 */
export function useUpsertPostmortem(incidentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertPostmortemRequest) =>
      apiFetch<PostmortemResponse>(`/incidents/${incidentId}/postmortem`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(postmortemKeys.byIncident(incidentId), updated);
      void queryClient.invalidateQueries({
        queryKey: incidentKeys.timeline(incidentId),
      });
    },
  });
}

/** POST /incidents/{id}/postmortem/followup-tasks (F-008). */
export function useCreateFollowupTask(incidentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateFollowupTaskRequest) =>
      apiFetch<FollowupTaskResponse>(
        `/incidents/${incidentId}/postmortem/followup-tasks`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: postmortemKeys.byIncident(incidentId),
      });
    },
  });
}

export interface UpdateFollowupTaskVars {
  taskId: string;
  body: UpdateFollowupTaskRequest;
}

/** PATCH /followup-tasks/{taskId} - field edits (title/assignee/due date). */
export function useUpdateFollowupTask(incidentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, body }: UpdateFollowupTaskVars) =>
      apiFetch<FollowupTaskResponse>(`/followup-tasks/${taskId}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: postmortemKeys.byIncident(incidentId),
      });
    },
  });
}

export interface ChangeFollowupTaskStatusVars {
  taskId: string;
  status: FollowupStatus;
}

/** PATCH /followup-tasks/{taskId}/status (AC-009, audited server-side). */
export function useChangeFollowupTaskStatus(incidentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, status }: ChangeFollowupTaskStatusVars) =>
      apiFetch<FollowupTaskResponse>(`/followup-tasks/${taskId}/status`, {
        method: "PATCH",
        body: JSON.stringify({
          status,
        } satisfies ChangeFollowupTaskStatusRequest),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: postmortemKeys.byIncident(incidentId),
      });
    },
  });
}
